import { IRawStyle } from "@fluentui/react";
import React, { useState } from "react";
import { Badge, BadgeColor, GridColumn, GridRow, Pivot, PivotItem, Stack } from "sprint-common-web";

import { LanguagesDataProvider } from "../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { AssetType } from "../../../common/types/AssetType";
import { NoteSubjectType } from "../../../common/types/NoteSubjectType";
import { RecordType } from "../../../common/types/RecordType";
import { ConformingAssetsSection } from "../common/conforming-assets/ConformingAssetsSection";
import { NotesSection } from "../common/notes/NotesSection";
import { useOnPremAssetDetails } from "./common/hooks/useOnPremAssetDetails";
import { OnPremContentSection } from "./content/OnPremContentSection";
import { OnPremLanguagesSection } from "./languages/OnPremLanguagesSection";
import { OnPremLibrarySection } from "./library/OnPremLibrarySection";
import { OnPremAssetDetailsTab } from "./OnPremAssetDetails";
import { OnPremAssetStatus } from "./OnPremAssetStatus";
import { OnPremTechnicalSection } from "./technical/OnPremTechnicalSection";

export const OnPremAssetDetailsSections: React.FunctionComponent = () => {
    const [currentTab, setCurrentTab] = useState<OnPremAssetDetailsTab>(OnPremAssetDetailsTab.Info);
    const { data: assetDetails } = useOnPremAssetDetails();

    const noteSectionWidth = assetDetails?.type === RecordType.Subtitle ? 12 : 6;

    return (
        <Stack>
            <Pivot
                selectedKey={String(currentTab)}
                onLinkClick={(item) => setCurrentTab(Number(item?.props.itemKey))}
                key={String(currentTab)}
            >
                <PivotItem headerText="Info" itemKey={String(OnPremAssetDetailsTab.Info)}>
                    <Stack styles={{ root: defaultStyles }}>
                        <GridRow>
                            <GridColumn sm={7}>
                                <OnPremLibrarySection />
                            </GridColumn>
                            <GridColumn sm={5}>
                                <OnPremContentSection />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn sm={6}>
                                <OnPremTechnicalSection />
                            </GridColumn>
                            <GridColumn sm={noteSectionWidth}>
                                <NotesSection
                                    subjectType={NoteSubjectType.OnPremAsset}
                                    renderBadge={() => (
                                        <Badge
                                            text="On-Prem"
                                            color={BadgeColor.Blue}
                                            size={12}
                                            testId="note-on-prem-badge"
                                            tooltipContent="On-Prem asset note"
                                        />
                                    )}
                                />
                            </GridColumn>
                        </GridRow>
                        <GridRow>
                            <GridColumn sm={12}>
                                <LanguagesDataProvider>
                                    <OnPremLanguagesSection />
                                </LanguagesDataProvider>
                            </GridColumn>
                        </GridRow>
                    </Stack>
                </PivotItem>
                <PivotItem headerText="Assets" itemKey={String(OnPremAssetDetailsTab.Assets)}>
                    <Stack styles={{ root: defaultStyles }}>
                        <GridRow>
                            <GridColumn sm={12}>
                                <ConformingAssetsSection
                                    assetType={AssetType.OnPrem}
                                    canBeDeprecated={assetDetails?.status !== OnPremAssetStatus.Deprecated}
                                    contentVersionIds={assetDetails?.content?.versionSummary.ids ?? []}
                                />
                            </GridColumn>
                        </GridRow>
                    </Stack>
                </PivotItem>
            </Pivot>
        </Stack>
    );
};

const defaultStyles: IRawStyle = {
    paddingTop: 20
};
